import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// Styles
import "./styles.scss";

// Redux
import { useDispatch, useSelector } from "react-redux";
import {
  getAllAssessmentRequest_ADMIN,
  notifyAssessor,
} from "../../../../Redux/Slices/Admin/adminSlice";

// Custom layout
import MainLayout from "Layouts/Main";

// Assets
import Colors from "../../../../Assets/SassConstants/colors.scss";
import More from "../../../../Assets/Svg/Admin/AssessmentBacklog/more.svg";
import NotifyIcon from "../../../../Assets/Svg/Admin/AssessmentBacklog/notify.svg";
import ViewIcon from "../../../../Assets/Svg/Admin/AssessmentBacklog/view.svg";
import EditIcon from "../../../../Assets/Svg/Admin/AssessmentBacklog/edit.svg";
import InputBox from "Components/InputBox";

// Custom component
import Pagination from "../../../../Components/Pagination";
import NormalTable from "Components/NormalTable";
import { notifySuccess } from "Services/Utils/toastNotification";
import CustomSelect from "../../../../Components/CustomSelect";
import StatusBadge from "../../../../Components/StatusBadge";

const AssessmentBacklog = () => {
  // Initialize useNavigate
  const navigate = useNavigate();

  //Initialize redux
  const dispatch = useDispatch();

  //==================== List screen start ====================
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [assessmentList, setAssessmentList] = useState([]);
  const [activeRow, setActiveRow] = useState(null);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({
    sortBy: null,
    sortOrder: 0,
  });
  const [selectedOption, setSelectedOption] = useState("");
  const [noDataAvailable, setNoDataAvailable] = useState(false);

  const filterOptions = [
    { label: "All", value: "" },
    { label: "Pending with User", value: "Pending with User" },
    { label: "Pending with Assessor", value: "Pending with Assessor" },
    { label: "Pending with Approver", value: "Pending with Approver" },
    { label: "Completed", value: "Completed" },
  ];

  const filterStyles = {
    control: (provided) => ({
      ...provided,
      border: "1px solid #464646",
      borderRadius: "6px",
      padding: "4px",
      width: "237px",
      minHeight: "44px",
      height: "42px",
      cursor: "pointer",
      boxShadow: "4px 4px 8px rgba(0, 0, 0, 0.1)",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
          ? "#1F2155"
          : state.isFocused
              ? "#e9ecef"
              : "#fff",
      color: state.isSelected ? "#fff" : "#000",
      padding: 10,
      "&:hover": {
        backgroundColor: state.isSelected
            ? "#1F2155"
            : state.isFocused
                ? "#e9ecef"
                : "#fff",
        color: state.isSelected ? "#fff" : "#000",
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#000000",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: "#121212",
      padding: "0 8px",
    }),
  };

  const statusMap = {
    "Pending with User": {
      backgroundColor: "#FFE5A0",
      color: "#473821",
    },
    "Pending with Assessor": {
      backgroundColor: "#BFE1F6",
      color: "#0A53A8",
    },
    "Pending with Approver": {
      backgroundColor: "#D4EDBC",
      color: "#11734B",
    },
    "Completed": {
      backgroundColor: "#068262",
      color: "#FFFFFF",
    },
  };

  const menuRef = useRef(null);
  const isLoading = useSelector((state) => state.admin.getAllAssessmentisLoading);

  const handlePageChange = async (page) => {
    await setCurrentPage(page);
    await getAllAssessmentRequest_ADMIN_(page);
  };

  const handleMenuToggle = (rowIndex) => {
    setActiveRow(activeRow === rowIndex ? null : rowIndex);
  };

  const handleClickOutside = (event) => {
    if (menuRef.current && !menuRef.current.contains(event.target)) {
      setActiveRow(null);
    }
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1); // Reset to first page on new search
  };

  useEffect(() => {
    getAllAssessmentRequest_ADMIN_(currentPage);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [sort, search]);

  useEffect(() => {
    getAllAssessmentRequest_ADMIN_(1);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectedOption]);

  //To handle the index
  const getDisplayedIndex = (currentPage, index) => {
    // Calculate the starting index for the current page
    const startingIndex = (currentPage - 1) * 8 + 1;
    return startingIndex + index;
  };

  //To get all assessment details
  const getAllAssessmentRequest_ADMIN_ = (page) => {
    dispatch(
        getAllAssessmentRequest_ADMIN({
          page,
          search,
          sortBy: sort.sortOrder === 1 ? "ASC" : "DESC",
          orderBy: sort.sortBy,
          filerByStatus: selectedOption,
          callBack: getAllAssessmentRequestCB_ADMIN,
        })
    );
  };

  //To handle the response of getAllAssessmentRequest_ADMIN
  const getAllAssessmentRequestCB_ADMIN = (data) => {
    setTotalPages(data?.pageMeta?.totalPages);
    setAssessmentList(data?.data);
    setNoDataAvailable(data.length === 0);
  };

  //==================== List screen end ====================

  const assessmentBacklogHeader = [
    { label: "S.No", isSort: false, key: "sno" },
    { label: "Account Name", isSort: true, key: "company_name" },
    { label: "Reference Number", isSort: true, key: "reference_name", headerStyle: { paddingInline: "15px"} },
    { label: "Country", isSort: true, key: "country_name" },
    { label: "Industry", isSort: true, key: "industry_name" },
    // { label: "Status", isSort: false, key: "status" },
    { label: "Report Status", isSort: false, key: "reportStatus" },
    { label: "No.of Users", isSort: false, key: "noOfUsers" },
  ];

  const handleNotify = (id) => {
    dispatch(
        notifyAssessor({
          data: {
            backlog_id: id,
          },
          callBack: notifySuccess("Notified Successfully"),
        })
    );
  };

  return (
      <MainLayout style={{ backgroundColor: Colors.grey_12 }}>
        <div className="admin-container">
          {/* Heading and Button Section */}
          <div className="admin-row admin-header-row">
            <h1 className="admin-heading">Assessment Backlog</h1>
            <div className="d-flex flex-row align-items-center gap-2 me-3">
              <CustomSelect
                  defaultValue={filterOptions[0]}
                  options={filterOptions}
                  customStyles={filterStyles}
                  placeholder="Select Filter"
                  onChange={(data) => {
                    setSelectedOption(data?.value);
                  }}
              />


              <InputBox
                  name="search"
                  isSearch={true}
                  placeholder="Search"
                  value={search}
                  onChange={(e) => handleSearch(e)}
                  inputStyle={{ width: "277px" }}
              />
              <button
                  onClick={() => {
                    navigate("/assessment-backlogs/add");
                  }}
                  className="admin-add-button"
              >
                Add New
              </button>
            </div>

          </div>

          {/* Space */}
          <div className="admin-space"></div>
          {/* Combined Header and Dynamic Value Rows */}
          <div className="backlog-table">
            <NormalTable
                loading={isLoading === 'loading'}
                headers={assessmentBacklogHeader}
                sort={sort}
                setSort={setSort}
                currentPage={currentPage}
                noDataColspan={9}
                totalPages={totalPages}
                handlePageChange={handlePageChange}
                customStyle={{
                  customTableHeaderContainer: "customAdminBacklogTableHeader",
                  customHeaderTitle: "customAdminBacklogHeaderStartTitle",
                  customTable: "customAdminBacklogTable",
                  customTableContainer: "customAdminBacklogTableContainer",
                }}
                noDataLabel={noDataAvailable}
            >
              {assessmentList?.map((row, index) => {
                const isLastRow = index === assessmentList.length - 1;
                const isSecondLastRow = index === assessmentList.length - 2;
                return (
                    <tr key={index} style={{position: "relative"}}>
                      <td>
                        <div className="TableDivisionStart ms-5 ps-1 mt-1">
                          {getDisplayedIndex(currentPage, index)}
                        </div>
                      </td>
                      <td>
                        <div className="TableDivisionStart ms-2 mt-1">
                          {row.company_name ?? "-"}
                        </div>
                      </td>
                      <td>
                        <div className="TableDivisionStart ms-1 mt-1">
                          <div style={{width: "250px"}}>

                            {row.reference_name ?? "-"}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="TableDivisionStart ms-2 mt-1">
                          {row.country_name ?? "-"}
                        </div>
                      </td>
                      <td>
                        <div className="TableDivisionStart ms-2 mt-1">
                          {row.industry_name ?? "-"}
                        </div>
                      </td>
                      {/*<td>*/}
                      {/*  <div className="TableDivisionStart ms-2 mt-1">*/}
                      {/*    {row.status === 2 ? "Active" : "Inactive"}*/}
                      {/*  </div>*/}
                      {/*</td>*/}
                      <td>
                        <div className="TableDivisionStart ms-2 mt-1">
                          <StatusBadge
                              value={row.reportStatus}
                              statuses={statusMap}
                              className=""
                          />
                        </div>
                      </td>
                      <td>
                        <div className="TableDivisionStart ms-5 mt-1">
                          {row.user_count}
                        </div>
                      </td>
                      <td>
                        <div
                            className="TableDivisionStart ms-5 mt-2"
                            onClick={() => handleMenuToggle(index)}
                            style={{cursor: "pointer"}}
                        >
                          <img src={More} alt="More"/>
                          {activeRow === index && (
                              <div
                                  className="admin-menu-options"
                                  ref={menuRef}
                                  style={{
                                    ...((isLastRow || isSecondLastRow) && {top: "-63px"}),
                                  }}
                              >
                                <div
                                    className="admin-menu-option"
                                    onClick={() => handleNotify(row?.backlog_id)}
                                >
                                  <img src={NotifyIcon} alt="Notify Assessor"/>
                                  Notify Assessor
                                </div>
                                <div
                                    onClick={() => {
                                      navigate("/assessment-backlogs/view", {
                                        state: {
                                          selectedAssessmentId: row?.backlog_id,
                                        },
                                      });
                                    }}
                                    className="admin-menu-option"
                                >
                                  <img src={ViewIcon} alt="View"/>
                                  View
                                </div>
                                <div
                                    onClick={() => {
                                      navigate("/assessment-backlogs/update", {
                                        state: {
                                          selectedAssessmentId: row?.backlog_id,
                                        },
                                      });
                                    }}
                                    className="admin-menu-option"
                                >
                                  <img src={EditIcon} alt="View"/>
                                  Edit
                                </div>
                              </div>
                          )}
                        </div>
                      </td>
                    </tr>
                );
              })}
            </NormalTable>
            {/* {assessmentList?.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )} */}
        </div>
        </div>
      </MainLayout>
  );
};

export default AssessmentBacklog;
